<template>
  <Teleport to="body">
    <Transition name="modal-overlay">
      <div v-if="visible" class="modal-overlay fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center px-4 sm:px-6" @click="handleBackdropClick">
        <Transition name="modal-container">
          <div class="modal-container bg-white rounded-lg shadow-xl overflow-hidden w-full" :style="{ maxWidth: computedMaxWidth }" @click.stop>
            <!-- 模态框头部 -->
            <div :class="['modal-header sticky top-0 z-10 px-6 py-4 flex justify-between items-center', headerClass]">
              <h3 class="text-xl font-semibold">{{ title }}</h3>
              <button v-if="showCloseBtn" @click="closeModal" class="text-gray-500 hover:text-gray-700 focus:outline-none">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            
            <!-- 模态框内容 -->
            <div :class="['modal-body overflow-y-auto', bodyClass]" :style="{ maxHeight: maxHeight || '70vh' }">
              <div class="px-2 sm:px-4">
                <slot></slot>
              </div>
            </div>
            
            <!-- 模态框底部 -->
            <div v-if="$slots.footer" :class="['modal-footer sticky bottom-0 z-10 px-6 py-4 flex justify-end', footerClass]">
              <slot name="footer"></slot>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { Teleport, Transition, computed } from 'vue';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    default: ''
  },
  maxWidth: {
    type: String,
    default: '4xl'
  },
  maxHeight: {
    type: String,
    default: '70vh'
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true
  },
  showCloseBtn: {
    type: Boolean,
    default: true
  },
  headerClass: {
    type: String,
    default: 'bg-white border-b border-gray-200'
  },
  bodyClass: {
    type: String,
    default: 'p-6'
  },
  footerClass: {
    type: String,
    default: 'bg-gray-50 border-t border-gray-200'
  }
});

const emit = defineEmits(['close']);

// 计算最大宽度
const computedMaxWidth = computed(() => {
  const widthMap = {
    'sm': '24rem',     // 384px
    'md': '28rem',     // 448px
    'lg': '32rem',     // 512px
    'xl': '36rem',     // 576px
    '2xl': '42rem',    // 672px
    '3xl': '48rem',    // 768px
    '4xl': '56rem',    // 896px
    '5xl': '64rem',    // 1024px
    'full': 'calc(100vw - 2rem)' // 全宽但保留边距
  };
  
  return widthMap[props.maxWidth] || widthMap['4xl'];
});

const closeModal = () => {
  emit('close');
};

const handleBackdropClick = (event) => {
  if (event.target.classList.contains('modal-overlay') && props.closeOnBackdrop) {
    closeModal();
  }
};
</script>

<style scoped>
/* 过渡效果 */
.modal-overlay-enter-active,
.modal-overlay-leave-active {
  transition: opacity 0.3s ease;
}

.modal-overlay-enter-from,
.modal-overlay-leave-to {
  opacity: 0;
}

.modal-container-enter-active,
.modal-container-leave-active {
  transition: all 0.3s ease;
}

.modal-container-enter-from,
.modal-container-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* 确保模态框始终在顶层 */
.modal-overlay {
  z-index: 9999 !important;
}

/* 模态框内容区域样式 */
.modal-container {
  border-radius: 0.5rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  width: 100%;
  margin: 0 auto;
  max-height: calc(100vh - 2rem);
}

/* 模态框响应式边距 */
@media (max-width: 640px) {
  .modal-overlay {
    padding: 1rem;
  }
  
  .modal-container {
    max-height: calc(100vh - 2rem);
  }
  
  .modal-header h3 {
    font-size: 1.125rem;
  }
  
  .modal-body {
    max-height: 65vh !important;
  }
}
</style> 